import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, InfoOutlined } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CustomTextInput from '../../../components/CustomTextInput';
import SelectedItems from '../../../components/SelectedItems';
import {
  ACCESS_LEVEL_OPTIONS,
  ACCESS_LEVELS,
  BUSINESS_PROFILE_ACCESS_LEVEL_OPTIONS,
  BUSINESS_PROFILES_MODULE_KEY,
  DEVELOPER_DASHBOARD_MODULE_KEY,
  EINVOICING_MODULE_KEYS,
  ROLE_MODULES,
  applyDeveloperDashboardSelection,
  applyEinvoicingModuleSelection,
  buildDefaultPermissions,
  isEinvoicingModuleActive,
  mapToPermissionsPayload,
  permissionsToMap,
} from '../constants/roleModules';
import { useCreateRoleHandler } from '../dataHandlers/createRoleHandler';
import { useUpdateRoleHandler } from '../dataHandlers/updateRoleHandler';
import { useGetBusinessProfilesHandler } from '../../businessProfiles/dataHandlers/getBusinessProfilesHandler';
import BusinessProfileMultiSelect from './BusinessProfileMultiSelect';
import useBusinessProfileFilters from "../../businessProfiles/components/BusinessProfilesFilterHelper";

const modules =  {
  "sales": "Sales",
  "sales_tooltip": "Customers, Sales Invoice, Sales Credit Note, Sales Register, Proforma Invoice, Products/Services",
  "purchases": "Purchase",
  "purchases_tooltip": "Suppliers, Purchase Invoice, Purchase Credit Note, Purchase Register, Products/Services",
  "document_report": "Document Reports",
  "document_report_tooltip": "To use this permission, the user must also be granted access to either the Sales or Purchase module.",
  "vat_report": "VAT Reports",
  "vat_report_tooltip": "To use this permission, the user must also be granted access to both Sales and Purchase module.",
  "developer_dashboard": "Developer Dashboard",
  "developer_dashboard_tooltip": "Mutually exclusive with einvoicing modules on the same role — assign a separate role for Sales, Purchase, Document-Report and VAT Report access for selected Business Profiles.",
  "bulk_upload": "Bulk Upload",
  "bulk_upload_tooltip": "Required to view or upload bulk files. Resource permissions (sales, purchases, or register) still apply for each upload type."
};

const getProfileChipLabel = (profiles, profileId) => {
  const profile = profiles.find((item) => item?.profile_id === profileId);
  if (!profile) return profileId;

  const name = profile.party_name || profile.name || '';
  const tin = profile.tin || profile.party_tax_scheme?.company_id || '';
  return [name, tin].filter(Boolean).join(' | ') || profileId;
};

export default function RoleFormModal({ open, onClose, mode, roleData }) {
  const isView = mode === 'view';
  const isCreate = mode === 'create';

  const schema = Yup.object().shape({
    name: Yup.string().required( 'This field is required'),
  });

  const [permissionMap, setPermissionMap] = useState(buildDefaultPermissions());
  const [allowedBusinessProfileIds, setAllowedBusinessProfileIds] = useState([]);
  const [businessProfileError, setBusinessProfileError] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { name: '' },
  });
  const { currentQuery } = useBusinessProfileFilters({
    defaultQuery: {},
    searchOnChange: true,
  });

  const { businessProfiles, isLoading: isProfilesLoading } = useGetBusinessProfilesHandler({
    searchQuery: { ...currentQuery, page: 0, page_size: 100 },
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setPermissionMap(roleData?.permissions ? permissionsToMap(roleData.permissions) : buildDefaultPermissions());
      setAllowedBusinessProfileIds(roleData?.business_profile_ids ?? []);
      setBusinessProfileError(false);
      reset({ name: roleData?.name ?? '' });
    }
  }, [open, roleData, reset]);

  const { mutate: createRole, isPending: isCreating } = useCreateRoleHandler({ onSuccess: onClose });
  const { mutate: updateRole, isPending: isUpdating } = useUpdateRoleHandler({ onSuccess: onClose });

  const title = isCreate
      ?  'Create Role'
      : isView
          ? 'View Role'
          : 'Edit Role';

  const onSubmit = (data) => {
    const payload = {
      name: data.name.trim(),
      permissions: mapToPermissionsPayload(permissionMap),
      business_profile_ids: allowedBusinessProfileIds,
    };
    if(!permissionMap[DEVELOPER_DASHBOARD_MODULE_KEY] && allowedBusinessProfileIds.length === 0) {
      setBusinessProfileError(true);
      return;
    }
    if (isCreate) {
      createRole(payload);
      return;
    }
    updateRole({ roleId: roleData.id, payload });
  };

  const isPending = isCreating || isUpdating;
  const businessProfilesAccessLevel = permissionMap[BUSINESS_PROFILES_MODULE_KEY] || ACCESS_LEVELS.READ;
  const developerDashboardLevel = permissionMap[DEVELOPER_DASHBOARD_MODULE_KEY] || ACCESS_LEVELS.NONE;
  const isDeveloperDashboardActive = developerDashboardLevel > ACCESS_LEVELS.NONE;
  const isEinvoicingActive =
      isEinvoicingModuleActive(permissionMap) ||
      allowedBusinessProfileIds.length > 0 ||
      businessProfilesAccessLevel === ACCESS_LEVELS.WRITE;
  const isBusinessProfileSelectDisabled = isView || isDeveloperDashboardActive;
  const isBusinessProfileAccessDisabled = isView || isDeveloperDashboardActive;
  const isDeveloperDashboardDisabled = isView || isEinvoicingActive;
  const handleAccessChange = (moduleKey, value) => {
    if (moduleKey === DEVELOPER_DASHBOARD_MODULE_KEY) {
      setPermissionMap((prev) => applyDeveloperDashboardSelection(prev, value));
      if (value > ACCESS_LEVELS.NONE) {
        setAllowedBusinessProfileIds([]);
      }
      return;
    }

    if (moduleKey === BUSINESS_PROFILES_MODULE_KEY) {
      setPermissionMap((prev) => applyEinvoicingModuleSelection(prev, moduleKey, value));
      return;
    }

    if (EINVOICING_MODULE_KEYS.includes(moduleKey)) {
      setPermissionMap((prev) => applyEinvoicingModuleSelection(prev, moduleKey, value));
    }
  };

  const handleBusinessProfileIdsChange = (ids) => {
    setAllowedBusinessProfileIds(ids);
    setBusinessProfileError(false);
    if (ids.length > 0) {
      setPermissionMap((prev) => ({
        ...prev,
        [DEVELOPER_DASHBOARD_MODULE_KEY]: ACCESS_LEVELS.NONE,
      }));
    }
  };

  return (
      <Modal open={open} onClose={onClose}>
        <Paper
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '40vw' },
              maxWidth: '900px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              outline: 'none',
            }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{title}</Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
          <Divider sx={{ my: 1 }} />

          <Box sx={{ overflowY: 'auto', flex: 1, pr: 0.5 }}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  {'Role Name'}
                </Typography>
                <CustomTextInput
                    name="name"
                    label=""
                    control={control}
                    errors={errors}
                    readOnly={isView}
                    placeholder={'e.g. Sales Manager'}
                />
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {'Permissions'}
                </Typography>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {ROLE_MODULES.map(({ key, infoTooltip , label}, index) => {
                    const isEinvoicingModule = EINVOICING_MODULE_KEYS.includes(key);
                    const isModuleDisabled =
                        isView ||
                        (key === DEVELOPER_DASHBOARD_MODULE_KEY && isDeveloperDashboardDisabled) ||
                        (isEinvoicingModule && isDeveloperDashboardActive);

                    return (
                        <Box
                            key={key}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              px: 2,
                              py: 1.5,
                              borderBottom: index < ROLE_MODULES.length - 1 ? 1 : 0,
                              borderColor: 'divider',
                            }}
                        >
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2">{ label}</Typography>
                            {infoTooltip && modules[`${key}_tooltip`] && (
                                <Tooltip title={modules[`${key}_tooltip`]} arrow>
                                  <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                                </Tooltip>
                            )}
                          </Stack>
                          <FormControl size="small" sx={{ minWidth: 160 }} disabled={isModuleDisabled}>
                            <Select
                                value={permissionMap[key] ?? 0}
                                onChange={(e) => handleAccessChange(key, e.target.value)}
                                sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                            >
                              {ACCESS_LEVEL_OPTIONS.map(({ value, labelKey }) => (
                                  <MenuItem key={value} value={value}>
                                    {labelKey}
                                  </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {'Business Profile Access'}
                    </Typography>
                    <Tooltip
                        title={
                          'Please select a business profile to assign to this role. If no selection is made, no profile will be assigned.'
                        }
                        arrow
                    >
                      <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Tooltip>
                  </Stack>
                  <FormControl size="small" sx={{ minWidth: 160 }} disabled={isBusinessProfileAccessDisabled}>
                    <Select
                        value={businessProfilesAccessLevel}
                        onChange={(e) => handleAccessChange(BUSINESS_PROFILES_MODULE_KEY, e.target.value)}
                        sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                    >
                      {BUSINESS_PROFILE_ACCESS_LEVEL_OPTIONS.map(({ value, labelKey }) => (
                          <MenuItem key={value} value={value}>
                            { labelKey}
                          </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {'Select the business profiles this role can access.'}
                </Typography>

                <Box sx={{ mt: 1.5 }}>
                  <BusinessProfileMultiSelect
                      profiles={businessProfiles}
                      selected={allowedBusinessProfileIds}
                      isLoading={isProfilesLoading}
                      disabled={isBusinessProfileSelectDisabled}
                      onChange={handleBusinessProfileIdsChange}
                  allProfilesLabel={'No Business Profiles Selected'}
                  selectedCountLabel={'{count} Business Profile(s) selected'}
                  selectAllLabel={'Select All {count} Business Profiles'}
                  errors={businessProfileError}
                />
                <SelectedItems
                  items={allowedBusinessProfileIds}
                  getLabel={(profileId) => getProfileChipLabel(businessProfiles, profileId)}
                  onRemove={(profileId) =>
                    handleBusinessProfileIdsChange(allowedBusinessProfileIds.filter((id) => id !== profileId))
                  }
                />
              </Box>
            </Box>
          </Stack>
        </Box>

          {!isView && (
              <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 1, mt: 2, pt: 1 }}>
                <Button onClick={onClose} disabled={isPending}>
                  { 'Cancel'}
                </Button>
                <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isPending}>
                  {isPending
                      ?  'Processing...'
                      : isCreate
                          ? 'Create Role'
                          : 'Save Changes'}
                </Button>
              </Stack>
          )}
        </Paper>
      </Modal>
  );
}